import { Col, Empty, Row, Spin, Tabs, Tooltip } from 'antd';
import { ExclamationCircleFilled, SyncOutlined } from '@ant-design/icons'
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import NotSignedIcon from '../../../images/NotSignedIcon.svg';
import SignedIcon from '../../../images/SignedIcon.svg';
import ExamDetailPage from "../../ExamPage/ExamDetailPage";
import ExpertRecords from "./ExpertRecords";
import groupedExams from "../../../utils/groupedExams"

const ExamTabs = ({
  loading,
  services,
  from,
  countryCode,
  serviceParams,
  refreshMedicalConsultationDetails,
  reprovedResultsMap = new Map(),
  currentServiceId,
  countrySettings,
  examListInSidebar = false,
  activeExamTab,
  setActiveExamTab,
  setExamTabItems,
  examContentRef,
}) => {
  const [itemsFiltered, setItemsFiltered] = useState([]);
  const [currentExamTab, setCurrentExamTab] = useState({});
  const isFirstLoadRef = useRef(true);
  const [consultationExamsData, setConsultationExamsData] = useState([]);

  const handleCurrentTabExams = (activeKey) => {
    setActiveExamTab(activeKey);

    const findExamIndex = itemsFiltered.map(item => parseInt(item.admission_patient_exam_id)).indexOf(parseInt(activeKey));
    setCurrentExamTab(itemsFiltered[findExamIndex]);
  };

  const selectInitialTab = (services) => {
    if (!services?.length) return;

    const firstService = services[0];
    const isPsychological = firstService.department_id === 2;

    if (!isPsychological || !['created', 'reevaluate'].includes(firstService.admission_patient_exam_state)) {
      setActiveExamTab(firstService.admission_patient_exam_id.toString());
      setCurrentExamTab(firstService);
    }
  }

  const itemHasReprovedValues = useMemo(() => {
    return (item) => {
      const getExamIdToCompare = (exam) => {
        return exam.admission_patient_exam_state === 'created'
          ? exam.admission_patient_exam_parent_id
          : exam.admission_patient_exam_id;
      };
      if (Object.keys(groupedExams).includes(item.exam_type)) {
        const matchingServices = services?.filter(service =>
          service.exam_type === item.exam_type
        );


        for (const [key, reprovedItems] of reprovedResultsMap.entries()) {
          if (key !== currentServiceId) continue;
          const hasMatch = reprovedItems.some(reprovedItem =>
            matchingServices.some(service =>
              reprovedItem.admissionPatientExamId === getExamIdToCompare(service)
            )
          );

          if (hasMatch) {
            return true;
          }
        }
      } else {
        for (const [key, reprovedItems] of reprovedResultsMap.entries()) {
          if (key !== currentServiceId) continue;
          const hasMatch = reprovedItems.some(reprovedItem =>
            reprovedItem.admissionPatientExamId === getExamIdToCompare(item)
          );

          if (hasMatch) {
            return true;
          }
        }
      }

      return false;
    };
  }, [reprovedResultsMap, services, currentServiceId]);


  useEffect(() => {
    if (services?.length) {
      const processedGroups = new Set();

      const filteredAndGrouped = services.reduce((accumulator, service) => {
        const groupName = groupedExams[service.exam_type];

        if (groupName) {
          if (!processedGroups.has(service.exam_type)) {
            processedGroups.add(service.exam_type);
            const serviceWithSign = services.find(s => s.exam_type === service.exam_type && s.need_expert_sign)
            const reevaluationService = services.find(
              (groupService) => groupService.exam_type === service.exam_type && groupService.extra_data?.reevaluation_type
            )
            accumulator.push({
              ...service,
              ...(serviceWithSign && { admission_patient_exam_id: serviceWithSign.admission_patient_exam_id }),
              name: groupName,
              need_expert_sign: !!serviceWithSign,
              is_signed: serviceWithSign?.is_signed,
              ...(reevaluationService && {
                extra_data: { ...service.extra_data, reevaluation_type: reevaluationService.extra_data.reevaluation_type },
              }),
            });
          }
        } else {
          accumulator.push(service);
        }

        return accumulator;
      }, [])

      const sortedItems = filteredAndGrouped.sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setItemsFiltered(sortedItems);

      if (examListInSidebar && setExamTabItems) {
        setExamTabItems(sortedItems, itemHasReprovedValues);
      }

      if (isFirstLoadRef.current) {
        selectInitialTab(sortedItems);
        isFirstLoadRef.current = false;
      }
    }
  }, [services, examListInSidebar, setExamTabItems]);

  useEffect(() => {
    if (examListInSidebar && setExamTabItems && itemsFiltered.length) {
      setExamTabItems(itemsFiltered, itemHasReprovedValues);
    }
  }, [itemHasReprovedValues, examListInSidebar, setExamTabItems]);

  useEffect(() => {
    if (examListInSidebar && activeExamTab && itemsFiltered.length) {
      const findExamIndex = itemsFiltered.map(item => parseInt(item.admission_patient_exam_id)).indexOf(parseInt(activeExamTab));
      if (findExamIndex !== -1) {
        setCurrentExamTab(itemsFiltered[findExamIndex]);
      }
    }
  }, [activeExamTab, itemsFiltered, examListInSidebar]);

  const renderExamContent = useCallback((item) => {
    const examKey = Object.keys(groupedExams).includes(item.exam_type)
      ? item.exam_type
      : (item.admission_patient_exam_parent_id || item.admission_patient_exam_id);
    return (
      <Row justify="center">
        <Col lg={24} xs={24} className="form-wrapper">
          <ExamDetailPage
            key={examKey}
            from={from}
            fromExternal={true}
            consultationExamsData={consultationExamsData}
            setConsultationExamsData={setConsultationExamsData}
            services={services}
            externalAdmissionPatientExamId={
              Object.keys(groupedExams).includes(item.exam_type)
                ? undefined
                : item.admission_patient_exam_parent_id ||
                item.admission_patient_exam_id
            }
            externalExam={item}
            serviceParams={serviceParams}
            externalGroupType={
              Object.keys(groupedExams).includes(
                item.exam_type
              ) && item.exam_type
            }
            inSidebarMode={examListInSidebar}
            refreshMedicalConsultationDetails={
              refreshMedicalConsultationDetails
            }
          />
        </Col>
      </Row>
    );
  }, [from, consultationExamsData, services, serviceParams, countryCode, refreshMedicalConsultationDetails, examListInSidebar]);

  return (
    <Col
      lg={24}
      xs={24}
      className={`exam-tabs ${examListInSidebar ? 'exam-tabs--sidebar-mode' : ''} ${countrySettings?.medicalConsultation?.examTabs?.margin?.[countryCode] || 'pt-50'
        }`}
    >
      {examListInSidebar && <div ref={examContentRef} className="exam-content-anchor" />}
      <Spin className="cmt-spin" spinning={loading.detail}>
        <Row justify="center" className="exam-detail">
          {currentExamTab?.admission_patient_exam_id &&
            currentExamTab?.need_expert_sign && (
              <Col lg={24} xs={24}>
                <ExpertRecords currentExamTab={currentExamTab} />
              </Col>
            )}
          {!examListInSidebar && (
            <Col lg={24} xs={24}>
              <h3 className="mb-30">Exámenes</h3>
            </Col>
          )}
          {itemsFiltered?.length === 0 ? (
            <Col lg={24} xs={24}>
              <Empty
                description={
                  <span style={{ fontSize: 12 }}>
                    No hay exámenes realizados
                  </span>
                }
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            </Col>
          ) : (
            <Col lg={24} xs={24}>
              <Tabs
                className="cmt-tabs exam-tabs-fixed-width"
                onChange={handleCurrentTabExams}
                activeKey={activeExamTab?.toString()}
                tabPosition="left"
              >
                {itemsFiltered?.map((item) => (
                  <Tabs.TabPane
                    key={item.admission_patient_exam_id}
                    tab={
                      <Row gutter={5} className="tab-row-container">
                        {
                          itemHasReprovedValues(item) && (
                            <Col style={{ width: '20px', textAlign: 'left' }}>
                              <Tooltip
                                title="Valor reprobatorio"
                                color="#223A70"
                                placement="right"
                                overlayClassName="sign-status-tooltip"
                              >
                                <ExclamationCircleFilled
                                  style={{
                                    color: "#D02A2A",
                                    fontSize: 16,
                                  }}
                                />
                              </Tooltip>
                            </Col>
                          )
                        }
                        <Col className="tab-title-col">
                          <Tooltip title={item.name} placement="left">
                            <p className="mb-0 tab-title">{item.name}</p>
                          </Tooltip>
                        </Col>
                        {
                          countrySettings?.medicalConsultation?.reevaluationTags?.[countryCode]
                          && (item.extra_data?.reevaluation_type === 'reevaluation' || item.extra_data?.reevaluation_type === 'observation_lift') && (
                            <Col>
                              <Tooltip
                                title={item.extra_data?.reevaluation_type === 'reevaluation' ? 'Reevaluación' : 'Levantamiento'}
                                placement="right"
                              >
                                <SyncOutlined className="reevaluation-cycle-icon" />
                              </Tooltip>
                            </Col>
                          )
                        }
                        {
                          item.need_expert_sign && (
                            <Col>
                              <img
                                src={item.is_signed ? SignedIcon : NotSignedIcon}
                                alt="Icono de firma"
                                width={20}
                              />
                            </Col>
                          )
                        }
                      </Row>
                    }
                  >
                    {renderExamContent(item)}
                  </Tabs.TabPane>
                ))}
              </Tabs>
            </Col>
          )}
        </Row>
      </Spin>
    </Col>
  );
};

export default ExamTabs;
